import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Input,
  Form,
  Typography,
  message,
  Row,
  Col,
  Divider,
  Space,
  Upload,
  Select,
  Checkbox,
  Tabs,
  Table,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { FormInstance } from "antd/es/form";
import { DeleteOutlined, UploadOutlined, LeftOutlined } from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";

import {
  Division,
  SaveDivisionParams,
  fetchDivisionByIdThunk,
  saveDivision,
} from "./actions";
import { getDivisionById } from 'app/shared/api/getDivisionById';
import { getReportTextReferenceData } from "app/shared/api/getReportTextReferenceData";

import { getAllCountries } from 'app/shared/api/getCountries';
import { getAllBookingSystems } from 'app/shared/api/getBookingSystems';
import { getActiveDivisions } from 'app/shared/api/getDivision';
import { updateBookingSystemDivisionExclusions, getExcludedBookingSystemIdsForDivision } from 'app/shared/api/bookingSystemExclusions';
import { Country, BookingSystem } from 'app/shared/model';
import UnsavedChangesModal from 'app/shared/ui/UnsavedChangesModal';
import {
  ENTITY_NAME_MAX_LENGTH,
  divisionEntityNameNoSpecialCharsRule,
  divisionNameMaxLengthRule,
} from "app/shared/validation/entityNameCharacters";
import RichTextEditor, { cleanRichTextValue, getRichTextPlainLength } from 'app/shared/ui/RichTextEditor';

import {
  getDivisionSaving,
  getDivisionList,
} from "./selectors";
import {
  buildDivisionReportTextsPayload,
  buildEmptyReportTextFormState,
  buildReportTextTemplates,
  mapDivisionReportTextsToFormState,
  reportTextTemplateRestrictsStudentRecipient,
  ReportTextFormState,
  ReportTextTemplate,
} from "./reportTextHelpers";

const { Title, Text } = Typography;
const { TextArea } = Input;

const MAX_BANNER_SIZE = 3 * 1024 * 1024;
const ACCEPTED_BANNER_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/webp"];
const ACCEPTED_BANNER_EXTENSIONS = [".png", ".jpg", ".jpeg", ".svg", ".webp"];

type BannerState = {
  image: string;
  contentType?: string;
  fileName?: string;
  preview?: string;
};

const guessMimeFromFilename = (filename?: string) => {
  const lower = (filename || "").toLowerCase();
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".svg")) return "image/svg+xml";
  if (lower.endsWith(".webp")) return "image/webp";
  return "image/png";
};

const ensureDataUrl = (image?: string, contentType?: string, fileName?: string) => {
  if (!image) return undefined;
  if (image.startsWith("data:")) return image;
  const mime = contentType || guessMimeFromFilename(fileName);
  return `data:${mime};base64,${image}`;
};

type DivisionFormSnapshot = {
  name: string;
  websiteUrl: string;
  termsAndConditions: string;
  groupsPaymentTerms: string;
  visaLetterNote: string;
  address: {
    id: string;
    line1: string;
    line2: string;
    line3: string;
    line4: string;
    countryISOCode: string;
    email?: string;
    telephone?: string;
  };
  excludedBookingSystemIds: string[];
  reportTextById: Record<string, string>;
};

const normalizeToString = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
};

const normalizeSelectionIds = (input: unknown): string[] => {
  if (!Array.isArray(input)) {
    return [];
  }
  return Array.from(
    new Set(
      input
        .map((item) => String(item).trim())
        .filter(Boolean)
    )
  ).sort();
};

const normalizeRichText = (value: unknown): string => {
  if (typeof value !== "string") {
    if (value === null || value === undefined) {
      return "";
    }
    value = String(value);
  }
  return cleanRichTextValue(value as string) ?? "";
};

const normalizeReportTextSnapshot = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== "object") {
    return {};
  }

  const entries = Object.entries(value as Record<string, any>);
  const normalized: Record<string, string> = {};
  entries.forEach(([key, item]) => {
    if (!item || typeof item !== "object") {
      normalized[key] = "";
      return;
    }
    const content = normalizeRichText(item?.content);
    const student = normalizeRichText(item?.studentContent);
    const agent = normalizeRichText(item?.agentContent);
    if (student !== "" || agent !== "") {
      normalized[key] = `${student}\u0000${agent}`;
    } else {
      normalized[key] = content;
    }
  });
  return normalized;
};

const collectFormSnapshot = (form: FormInstance): DivisionFormSnapshot | null => {
  try {
    const raw = form.getFieldsValue(true) ?? {};
    const address = raw.address ?? {};
    return {
      name: normalizeToString(raw.name),
      websiteUrl: normalizeToString(raw.websiteUrl),
      termsAndConditions: normalizeToString(raw.termsAndConditions),
      groupsPaymentTerms: normalizeToString(raw.groupsPaymentTerms),
      visaLetterNote: normalizeRichText(raw.visaLetterNote),
      address: {
        id: normalizeToString(address.id),
        line1: normalizeToString(address.line1),
        line2: normalizeToString(address.line2),
        line3: normalizeToString(address.line3),
        line4: normalizeToString(address.line4),
        countryISOCode: normalizeToString(address.countryISOCode).toUpperCase(),
        email: normalizeToString(address.email),
        telephone: normalizeToString(address.telephone),
      },
      excludedBookingSystemIds: normalizeSelectionIds(raw.excludedBookingSystemIds),
      reportTextById: normalizeReportTextSnapshot(raw.reportTextById),
    };
  } catch (error) {
    return null;
  }
};

const arraysEqual = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

const reportTextSnapshotEqual = (a: Record<string, string>, b: Record<string, string>): boolean => {
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).sort();
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if ((a[key] ?? "") !== (b[key] ?? "")) {
      return false;
    }
  }
  return true;
};

const snapshotsEqual = (a: DivisionFormSnapshot | null, b: DivisionFormSnapshot | null): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.name === b.name &&
    a.websiteUrl === b.websiteUrl &&
    a.termsAndConditions === b.termsAndConditions &&
    a.groupsPaymentTerms === b.groupsPaymentTerms &&
    a.visaLetterNote === b.visaLetterNote &&
    a.address.id === b.address.id &&
    a.address.line1 === b.address.line1 &&
    a.address.line2 === b.address.line2 &&
    a.address.line3 === b.address.line3 &&
    a.address.line4 === b.address.line4 &&
    a.address.countryISOCode === b.address.countryISOCode &&
    (a.address.email ?? "") === (b.address.email ?? "") &&
    (a.address.telephone ?? "") === (b.address.telephone ?? "") &&
    arraysEqual(a.excludedBookingSystemIds, b.excludedBookingSystemIds) &&
    reportTextSnapshotEqual(a.reportTextById, b.reportTextById)
  );
};

const bannersEqual = (a: BannerState | null, b: BannerState | null): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    (a?.image ?? "") === (b?.image ?? "") &&
    (a?.contentType ?? "") === (b?.contentType ?? "") &&
    (a?.fileName ?? "") === (b?.fileName ?? "")
  );
};

type UnsavedPromptState =
  | { type: "remove-banner" }
  | { type: "navigation"; proceed: () => void; reset: () => void; };

function parseDivisionDetailsRoute(pathname: string): { isAdd: boolean; divisionId?: number } {
  if (pathname.endsWith("/add")) {
    return { isAdd: true };
  }

  const match = pathname.match(/\/division-manager\/(\d+)\/?$/);
  const parsedId = match ? parseInt(match[1], 10) : undefined;

  return {
    isAdd: false,
    divisionId: Number.isFinite(parsedId) ? parsedId : undefined,
  };
}

const DivisionDetailsPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const saving = useSelector(getDivisionSaving);
  const list = useSelector(getDivisionList);
  const onSave = (payload: SaveDivisionParams) => dispatch(saveDivision(payload) as any);

  const navigate = useNavigate();
  const location = useLocation();
  const { isAdd, divisionId } = parseDivisionDetailsRoute(location.pathname);
  const isEditing = !isAdd && divisionId != null;

  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState<string>("general");
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(null);
  const [reportTextTemplates, setReportTextTemplates] = useState<ReportTextTemplate[]>([]);
  const [recipientAudienceByReportTextId, setRecipientAudienceByReportTextId] = useState<
    Record<number, "students" | "agents">
  >({});

  const recipientAudienceForSelected = useMemo(() => {
    if (selectedDocumentId == null) return "students";
    const doc = reportTextTemplates.find((d) => d.reportTextId === selectedDocumentId);
    const restricted = doc ? reportTextTemplateRestrictsStudentRecipient(doc) : false;
    const fallback: "students" | "agents" = restricted ? "agents" : "students";
    return recipientAudienceByReportTextId[selectedDocumentId] ?? fallback;
  }, [selectedDocumentId, recipientAudienceByReportTextId, reportTextTemplates]);

  useEffect(() => {
    if (selectedDocumentId == null) return;
    const doc = reportTextTemplates.find((d) => d.reportTextId === selectedDocumentId);
    if (!doc || !reportTextTemplateRestrictsStudentRecipient(doc)) return;
    setRecipientAudienceByReportTextId((prev) => {
      if (prev[selectedDocumentId] === "agents") return prev;
      return { ...prev, [selectedDocumentId]: "agents" };
    });
  }, [selectedDocumentId, reportTextTemplates]);

  const reportTextValues = Form.useWatch("reportTextById", form) as ReportTextFormState | undefined;
  const currentDocumentContent = useMemo(() => {
    if (!selectedDocumentId) return "";
    const entry = reportTextValues?.[String(selectedDocumentId)];
    if (!entry) return "";
    const doc = reportTextTemplates.find((d) => d.reportTextId === selectedDocumentId);
    if (doc?.supportsRecipientType) {
      return recipientAudienceForSelected === "agents"
        ? entry.agentContent ?? ""
        : entry.studentContent ?? "";
    }
    return entry.content ?? "";
  }, [selectedDocumentId, reportTextValues, reportTextTemplates, recipientAudienceForSelected]);
  
  const currentDocumentCharCount = useMemo(
    () => getRichTextPlainLength(currentDocumentContent),
    [currentDocumentContent]
  );

  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const countryOptions = useMemo(() => {
    return (countries || [])
      .map((country) => {
        const value = typeof country?.id === 'string' ? country.id.toUpperCase() : '';
        return value
          ? { value, label: country?.name ? `${country.name} (${value})` : value }
          : null;
      })
      .filter(Boolean) as { value: string; label: string }[];
  }, [countries]);
  const [bookingSystems, setBookingSystems] = useState<BookingSystem[]>([]);
  const [bookingSystemsLoading, setBookingSystemsLoading] = useState(false);
  const [initialExcludedBookingSystems, setInitialExcludedBookingSystems] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;
    setCountriesLoading(true);
    getAllCountries()
      .then((items) => {
        if (!mounted) return;
        setCountries(items || []);
      })
      .catch(() => {
        if (!mounted) return;
        setCountries([]);
      })
      .finally(() => {
        if (mounted) setCountriesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setBookingSystemsLoading(true);
    getAllBookingSystems()
      .then((systems) => {
        if (!mounted) return;
        setBookingSystems(systems || []);
      })
      .catch(() => {
        if (!mounted) return;
        setBookingSystems([]);
      })
      .finally(() => {
        if (mounted) {
          setBookingSystemsLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    getReportTextReferenceData("Division")
      .then((items) => {
        if (!mounted) return;
        const templates = buildReportTextTemplates(items);
        setReportTextTemplates(templates);
      })
      .catch(() => {
        if (!mounted) return;
        setReportTextTemplates([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const [banner, setBanner] = useState<BannerState | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [initialBanner, setInitialBanner] = useState<BannerState | null>(null);
  const initialFormSnapshotRef = useRef<DivisionFormSnapshot | null>(null);
  const [unsavedPrompt, setUnsavedPrompt] = useState<UnsavedPromptState | null>(null);
  const [divisionCentreId, setDivisionCentreId] = useState<number | null>(null);
  const reportTextIdsRef = useRef<Record<string, number>>({});

  const resetBannerState = React.useCallback(() => {
    setBanner(null);
    setBannerError(null);
  }, []);

  const convertBannerFromApi = (source?: Division["accreditationBanner"]): BannerState | null => {
    if (!source || !source.image) return null;

    const preview = ensureDataUrl(source.image, source.contentType, source.fileName);
    if (!preview) return null;

    let rawImage = source.image;
    if (source.image.startsWith("data:")) {
      const commaIdx = source.image.indexOf(",");
      rawImage = commaIdx >= 0 ? source.image.slice(commaIdx + 1) : source.image;
    }

    return {
      image: rawImage,
      contentType: source.contentType || guessMimeFromFilename(source.fileName),
      fileName: source.fileName,
      preview,
    };
  };

  const getBannerSrc = (current?: BannerState | null) => {
    if (!current) return undefined;
    if (current.preview) return current.preview;
    return ensureDataUrl(current.image, current.contentType, current.fileName);
  };

  const handleBannerBeforeUpload = (file: File) => {
    const mime = (file.type || "").toLowerCase();
    const name = (file.name || "").toLowerCase();
    const isTypeAllowed =
      ACCEPTED_BANNER_MIME_TYPES.includes(mime) ||
      ACCEPTED_BANNER_EXTENSIONS.some((ext) => name.endsWith(ext));

    if (!isTypeAllowed) {
      setBannerError("Accreditation banner must be PNG, JPEG, SVG, or WEBP.");
      message.error("Only PNG, JPEG, SVG, or WEBP files are allowed.");
      return Upload.LIST_IGNORE;
    }

    if (file.size > MAX_BANNER_SIZE) {
      setBannerError("Accreditation banner must be 3 MB or smaller.");
      message.error("File size must be 3 MB or less.");
      return Upload.LIST_IGNORE;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const contentType = file.type || guessMimeFromFilename(file.name);

      const commaIdx = result.indexOf(",");
      const raw = commaIdx >= 0 ? result.slice(commaIdx + 1) : result;

      setBanner({
        image: raw,
        contentType,
        fileName: file.name,
        preview: result,
      });
      setBannerError(null);
    };
    reader.onerror = () => {
      message.error("Failed to read the file. Please try again.");
    };
    reader.readAsDataURL(file);
    return Upload.LIST_IGNORE;
  };

  const handleRemoveBanner = React.useCallback(() => {
    setUnsavedPrompt({ type: "remove-banner" });
  }, []);

  const formValues = Form.useWatch([], form);  
  const pageHasUnsavedChanges = React.useMemo(() => {
    const initialSnapshot = initialFormSnapshotRef.current;
    const currentSnapshot = collectFormSnapshot(form);
    if (!currentSnapshot || !initialSnapshot) {
      return false;
    }
    const formChanged = !snapshotsEqual(initialSnapshot, currentSnapshot);
    const bannerChanged = !bannersEqual(initialBanner, banner);
    return formChanged || bannerChanged;
  }, [banner, form, initialBanner, formValues]);

  React.useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (pageHasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [pageHasUnsavedChanges]);

  useEffect(() => {
    if (!isEditing || !divisionId) {
      return;
    }

    let mounted = true;
    const loadDivision = async () => {
      try {
        let fresh: Division | null = null;
        try {
          fresh = await dispatch(fetchDivisionByIdThunk(divisionId) as any);
        } catch (thunkError) {
        }
        
        if (!fresh) {
          try {
            const apiResult = await getDivisionById(divisionId);
            if (apiResult) {
              const centreIdFromApi = apiResult.centreId ?? apiResult.centre?.id ?? null;
              if (centreIdFromApi) {
                setDivisionCentreId(centreIdFromApi);
              }
              fresh = {
                id: apiResult.id,
                name: apiResult.name ?? "",
                termsAndConditions: apiResult.termsAndConditions,
                groupsPaymentTerms: apiResult.groupsPaymentTerms,
                websiteUrl: apiResult.websiteUrl,
                address: apiResult.address,
                accreditationBanner: apiResult.accreditationBanner,
                isActive: apiResult.isActive ?? true,
                excludedBookingSystemIds: [],
                visaLetterNote: apiResult.visaLetterNote,
                divisionReportTexts: apiResult.divisionReportTexts,
              } as any;
            }
          } catch (directError) {
          }
        }
        if (!mounted) return;
        
        if (!fresh) {
          message.error("Division not found");
          navigate("/division-manager");
          return;
        }

        const centreIdFromDivision = (fresh as any).centreId ?? (fresh as any).centre?.id ?? null;
        const divisionReportTexts = (fresh as any).divisionReportTexts ?? [];
        const centreIdFromReportTexts = Array.isArray(divisionReportTexts) && divisionReportTexts.length > 0
          ? divisionReportTexts[0]?.centreId
          : null;
        
        const finalCentreId = centreIdFromDivision ?? centreIdFromReportTexts;
        if (finalCentreId) {
          setDivisionCentreId(finalCentreId);
        }

        let excluded: string[] = (fresh.excludedBookingSystemIds ?? []).map((id) => String(id));
        try {
          const latestExcluded = await getExcludedBookingSystemIdsForDivision(fresh.id);
          if (Array.isArray(latestExcluded) && latestExcluded.length >= 0) {
            excluded = latestExcluded.map((id) => String(id));
          }
        } catch (error) {
        }
        setInitialExcludedBookingSystems(excluded);

        const visaLetterNote = cleanRichTextValue(fresh.visaLetterNote) ?? "";
        const { reportTextById, existingIdsByReportTextRow } = mapDivisionReportTextsToFormState({
          templates: reportTextTemplates,
          divisionReportTexts,
        });
        reportTextIdsRef.current = existingIdsByReportTextRow;
        
        const formValues = {
          name: fresh.name ?? "",
          websiteUrl: fresh.websiteUrl ?? "",
          address: fresh.address ? {
            id: fresh.address.id,
            line1: fresh.address.line1 ?? "",
            line2: fresh.address.line2 ?? "",
            line3: fresh.address.line3 ?? "",
            line4: fresh.address.line4 ?? "",
            countryISOCode: fresh.address.countryISOCode ?? "",
            email: fresh.address.email ?? "",
            telephone: fresh.address.telephone ?? "",
          } : {
            id: undefined,
            line1: "",
            line2: "",
            line3: "",
            line4: "",
            countryISOCode: "",
            email: "",
            telephone: "",
          },
          termsAndConditions: fresh.termsAndConditions ?? "",
          groupsPaymentTerms: fresh.groupsPaymentTerms ?? "",
          visaLetterNote: visaLetterNote,
          isActive: fresh.isActive ?? true,
          excludedBookingSystemIds: excluded,
          reportTextById: reportTextById,
        };
        
        form.resetFields();
        form.setFieldsValue(formValues);
        
        setSelectedDocumentId((current) => {
          if (current && reportTextTemplates.some((x) => x.reportTextId === current)) {
            return current;
          }
          return reportTextTemplates[0]?.reportTextId ?? null;
        });

        const bannerFromApi = convertBannerFromApi(fresh.accreditationBanner);
        setBanner(bannerFromApi);
        setBannerError(null);
        setInitialBanner(bannerFromApi);
        
        const snapshot = collectFormSnapshot(form);
        if (snapshot) {
          initialFormSnapshotRef.current = snapshot;
        }
      } catch (error) {
        message.error("Failed to load division");
        navigate("/division-manager");
      }
    };
    loadDivision();
    return () => {
      mounted = false;
    };
  }, [isEditing, divisionId, dispatch, form, navigate, reportTextTemplates]);

  useEffect(() => {
    if (!isAdd) {
      return;
    }

    resetBannerState();
    setInitialExcludedBookingSystems([]);
    reportTextIdsRef.current = {};
    setInitialBanner(null);

    const frameId = requestAnimationFrame(() => {
      const currentYear = new Date().getFullYear();
      form.resetFields();
      form.setFieldsValue({
        name: "",
        websiteUrl: "",
        address: {
          id: undefined,
          line1: "",
          line2: "",
          line3: "",
          line4: "",
          countryISOCode: "",
          email: "",
          telephone: "",
        },
        termsAndConditions: "",
        groupsPaymentTerms: "",
        visaLetterNote: "",
        isActive: true,
        excludedBookingSystemIds: [],
        pricingYear: currentYear,
        reportTextById: buildEmptyReportTextFormState(reportTextTemplates),
      });

      const snapshot = collectFormSnapshot(form);
      if (snapshot) {
        initialFormSnapshotRef.current = snapshot;
      }
    });

    setSelectedDocumentId((current) => {
      if (current && reportTextTemplates.some((x) => x.reportTextId === current)) {
        return current;
      }
      return reportTextTemplates[0]?.reportTextId ?? null;
    });

    return () => cancelAnimationFrame(frameId);
  }, [isAdd, form, resetBannerState, reportTextTemplates]);

  const uniqueNameValidator = async (_: any, value?: string) => {
    const name = (value ?? "").trim().toLowerCase();
    if (!name) return Promise.resolve();
    const currentId = divisionId;
    const exists = list.some(
      (d) => (d.name || "").trim().toLowerCase() === name && d.id !== currentId
    );
    return exists
      ? Promise.reject(new Error("Division name must be unique."))
      : Promise.resolve();
  };

  const normalizeWebsiteUrl = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return trimmed;
    
    if (!trimmed.toLowerCase().startsWith("http://") && !trimmed.toLowerCase().startsWith("https://")) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  const websiteValidator = async (_: any, value?: string) => {
    const trimmed = (value ?? "").trim();
    if (!trimmed) {
      return Promise.reject(new Error("Please enter a website link"));
    }
    
    const normalized = normalizeWebsiteUrl(trimmed);
    
    try {
      const url = new URL(normalized);
      
      if (url.protocol !== "http:" && url.protocol !== "https:") {
        return Promise.reject(new Error("Enter a valid website address."));
      }
      
      const hostname = url.hostname;
      if (!hostname || hostname.length === 0) {
        return Promise.reject(new Error("Enter a valid website address."));
      }
      
      const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
      if (!domainRegex.test(hostname)) {
        return Promise.reject(new Error("Enter a valid website address."));
      }
      
      form.setFieldsValue({ websiteUrl: normalized });
      
      return Promise.resolve();
    } catch {
      return Promise.reject(new Error("Enter a valid website address."));
    }
  };

  const handleSave = async () => {
    try {
      await form.validateFields();

      const values = form.getFieldsValue(true);

      const websiteUrl = normalizeWebsiteUrl((values.websiteUrl ?? "").trim());
      const addressForm: any = values.address ?? {};
      const rawVisaLetterNote = values.visaLetterNote ?? "";
      const visaLetterNote = cleanRichTextValue(rawVisaLetterNote);
      const addressValues = [
        addressForm?.line1,
        addressForm?.line2,
        addressForm?.line3,
        addressForm?.line4,
        addressForm?.countryISOCode,
      ].map((part: string | undefined) => (part ?? "").trim());
      const hasAddress = addressValues.some((part) => part.length > 0);

      const addressPayload =
        values.address === null
          ? null
          : hasAddress
          ? {
              id: addressForm?.id ?? undefined,
              line1: addressValues[0] || undefined,
              line2: addressValues[1] || undefined,
              line3: addressValues[2] || undefined,
              line4: addressValues[3] || undefined,
              countryISOCode: addressValues[4] ? addressValues[4].toUpperCase() : undefined,
            }
          : null;

      const headOfficeEmailAddress = addressForm?.email ? String(addressForm.email).trim() || undefined : undefined;
      const headOfficeTelephoneNo = addressForm?.telephone ? String(addressForm.telephone).trim() || undefined : undefined;

      const bannerPayload = banner ? {
        image: banner.image,
        contentType: banner.contentType || guessMimeFromFilename(banner.fileName),
        fileName: banner.fileName,
      } : undefined;

      const divisionReportTexts = buildDivisionReportTextsPayload({
        templates: reportTextTemplates,
        reportTextById: values.reportTextById,
        existingIdsByReportTextRow: reportTextIdsRef.current,
        divisionId: isEditing ? divisionId! : null,
        centreId: divisionCentreId ?? null,
      });

      const base: SaveDivisionParams = {
        name: values.name.trim(),
        websiteUrl,
        address: addressPayload,
        accreditationBanner: bannerPayload,
        termsAndConditions: values.termsAndConditions ?? "",
        groupsPaymentTerms: values.groupsPaymentTerms ?? "",
        visaLetterNote,
        visaLetterNoteFormat: 1,
        isActive: typeof values.isActive === "boolean" ? values.isActive : true,
        pricingYear: isEditing ? undefined : (values.pricingYear as number | undefined),
        headOfficeEmailAddress,
        headOfficeTelephoneNo,
        divisionReportTexts: divisionReportTexts.length > 0 ? divisionReportTexts : [],
      } as any;

      const payload: SaveDivisionParams = isEditing && divisionId
        ? { id: divisionId, ...base, isActive: !!values.isActive }
        : base;

      const selectedSystemsRaw = form.getFieldValue("excludedBookingSystemIds") as (string | number)[] | undefined;
      const normalizedSelected = Array.isArray(selectedSystemsRaw)
        ? selectedSystemsRaw.map((id) => String(id))
        : [];

      const res = await onSave(payload);
      if (res.ok) {
        let savedDivisionId: number | undefined = res.id ?? divisionId ?? undefined;
        if (savedDivisionId === undefined) {
          try {
            const latest = await getActiveDivisions();
            const normalizedName = payload.name.trim().toLowerCase();
            const matched = Array.isArray(latest)
              ? latest.find(div => (div?.name ?? "").trim().toLowerCase() === normalizedName)
              : undefined;
            if (matched?.id != null) {
            savedDivisionId = matched.id;
          }
        } catch (error) {
        }
        }

        if (savedDivisionId !== undefined) {
          try {
            const updatedIds = await updateBookingSystemDivisionExclusions(
              savedDivisionId,
              normalizedSelected,
              initialExcludedBookingSystems
            );
            setInitialExcludedBookingSystems(updatedIds);
          } catch (err: any) {
            message.error(err?.message ?? "Failed to update booking system exclusions.");
            return;
          }
        } else if (normalizedSelected.length > 0 || initialExcludedBookingSystems.length > 0) {
          message.warning("Saved, but booking system exclusions could not be updated because the division id was unavailable.");
        }

        message.success(isEditing ? "Division updated." : "Division created.");
        navigate("/division-manager");
      } else {
        const errorMessage = res.message || "Failed to save division.";
        const lower = errorMessage.toLowerCase();
        const isDuplicateNameError =
          lower.includes("name") &&
          (lower.includes("duplicate") ||
            lower.includes("already exists") ||
            lower.includes("unique"));
        const isDivisionNameFieldError =
          isDuplicateNameError ||
          lower.includes("division name") ||
          /exceed(s)?\s+30|30\s+characters|special characters|only contain letters/i.test(
            errorMessage
          );

        if (isDivisionNameFieldError) {
          form.setFields([{ name: "name", errors: [errorMessage] }]);
        }
        message.error(errorMessage);
      }
    } catch (err: any) {
      if (!err?.errorFields) {
        message.error(err?.message || "Failed to save division.");
      }
    }
  };

  const handleCancel = () => {
    if (pageHasUnsavedChanges) {
      setUnsavedPrompt({ type: "navigation", proceed: () => navigate("/division-manager"), reset: () => {} });
    } else {
      navigate("/division-manager");
    }
  };

  const confirmUnsavedPrompt = React.useCallback(() => {
    setUnsavedPrompt((current) => {
      if (!current) {
        return null;
      }

      if (current.type === "navigation") {
        current.proceed();
      } else if (current.type === "remove-banner") {
        setBanner(null);
        setBannerError(null);
      }

      return null;
    });
  }, []);

  const dismissUnsavedPrompt = React.useCallback(() => {
    setUnsavedPrompt((current) => {
      if (current?.type === "navigation") {
        current.reset();
      }
      return null;
    });
  }, []);

  const handleTabChange = (key: string) => {
    setActiveTab(key);

    if (key === "report-text" && !selectedDocumentId) {
      setSelectedDocumentId(reportTextTemplates[0]?.reportTextId ?? null);
    }
  };

  const handleDocumentSelect = (documentId: number) => {
    setSelectedDocumentId(documentId);
  };

  const selectedDocument = useMemo(() => {
    return reportTextTemplates.find((doc) => doc.reportTextId === selectedDocumentId) || null;
  }, [selectedDocumentId, reportTextTemplates]);

  const selectedDocumentLocksAgentsOnlyType = useMemo(
    () =>
      selectedDocument
        ? reportTextTemplateRestrictsStudentRecipient(selectedDocument)
        : false,
    [selectedDocument]
  );

  const documentTableColumns: ColumnsType<ReportTextTemplate> = [
    {
      title: "Document",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: ReportTextTemplate) => (
        <a
          onClick={() => handleDocumentSelect(record.reportTextId)}
          style={{
            color: selectedDocumentId === record.reportTextId ? "#1890ff" : "#1890ff",
            fontWeight: selectedDocumentId === record.reportTextId ? 500 : 400,
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Used In",
      dataIndex: "usedIn",
      key: "usedIn",
    },
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <Link to="/division-manager" style={{ display: "inline-flex", alignItems: "center", marginBottom: 8, color: "#1890ff" }}>
            <LeftOutlined style={{ marginRight: 4 }} />
            Back to divisions
          </Link>
          <Title level={2} style={{ margin: 0 }}>
            {isEditing ? "Update Division" : "Add Division"}
          </Title>
        </div>
        <Space>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            Save
          </Button>
        </Space>
      </div>

      <Form form={form} layout="vertical">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: "general",
              label: "General Information",
              children: (
                <>
                  <Form.Item
                    label="Division Name"
                    name="name"
                    validateTrigger={["onChange", "onBlur"]}
                    rules={[
                      { required: true, message: "Please enter a division name" },
                      divisionEntityNameNoSpecialCharsRule,
                      divisionNameMaxLengthRule,
                      { validator: uniqueNameValidator },
                    ]}
                  >
                    <Input
                      placeholder="Enter unique division name"
                      maxLength={30}
                      showCount={{
                        formatter: ({ count }) => (
                          <span
                            style={{
                              color: count > ENTITY_NAME_MAX_LENGTH ? "#ff4d4f" : undefined,
                            }}
                          >
                            {count}/{ENTITY_NAME_MAX_LENGTH}
                          </span>
                        ),
                      }}
                    />
                  </Form.Item>

      <Form.Item
        label="Website Link"
        name="websiteUrl"
        rules={[
          { required: true, message: "" },
          { validator: websiteValidator },
        ]}
      >
        <Input 
          placeholder="https://ecenglish.com" 
          allowClear 
          onBlur={(e) => {
            const value = e.target.value.trim();
            if (value && !value.toLowerCase().startsWith("http://") && !value.toLowerCase().startsWith("https://")) {
              form.setFieldsValue({ websiteUrl: `https://${value}` });
            }
          }}
        />
      </Form.Item>

      {!isEditing && (
        <Form.Item
          label="Pricing Year"
          name="pricingYear"
          rules={[
            { required: true, message: "Please select a pricing year" },
          ]}
          tooltip="Select the year when this division will start. A pricing year will be created automatically for the selected year."
        >
          <Select
            placeholder="Select pricing year"
            options={(() => {
              const currentYear = new Date().getFullYear();
              return [
                { label: String(currentYear), value: currentYear },
                { label: String(currentYear + 1), value: currentYear + 1 }
              ];
            })()}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )}

      <Form.Item name={["address", "id"]} hidden>
        <Input type="hidden" />
      </Form.Item>

      <Divider titlePlacement="start" style={{ marginBottom: 12 }}>
        Head Office Address
      </Divider>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item label="Building/Apartment" name={["address", "line1"]} rules={[{ max: 150, message: "Building must be less or equal 150 characters" }]}>
            <Input placeholder="Building / Apartment" maxLength={150} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Street" name={["address", "line2"]} rules={[{ max: 100, message: "Street must be less or equal 100 characters" }]}>
            <Input placeholder="Street" maxLength={100} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item label="City" name={["address", "line3"]} rules={[{ max: 50, message: "City must be less or equal 50 characters" }]}>
            <Input placeholder="City" maxLength={50} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Postal Code" name={["address", "line4"]} rules={[{ max: 100, message: "Postal Code must be less or equal 100 characters" }]}>
            <Input placeholder="Postal Code" maxLength={100} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} md={12}>
         <Form.Item label="Country" name={["address", "countryISOCode"]}
            rules={[{ required: true, message: "Address country is required" }]}
            required
          >
            <Select
              placeholder="Select country"
              allowClear
              showSearch
              optionFilterProp="label"
              options={countryOptions}
              loading={countriesLoading}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Head Office Email Address" 
            name={["address", "email"]}
            rules={[{ type: 'email' as const, message: 'Enter a valid email address' }]}
          >
            <Input placeholder="email@example.com" type="email" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Head Office Telephone Number" 
            name={["address", "telephone"]}
            rules={[{
              pattern: /^[+]?[\d\s().-]{7,20}$/,
              message: 'Enter a valid telephone number',
            }]}
          >
            <Input placeholder="e.g. +44 20 1234 5678" />
          </Form.Item>
        </Col>
      </Row>

      <Divider titlePlacement="start" style={{ marginBottom: 12 }}>
        Accreditation banner
      </Divider>

      <Form.Item
        label="Accreditation Banner"
        validateStatus={bannerError ? "error" : undefined}
        help={bannerError ?? ""}
      >
        <Space orientation="vertical" size={8} style={{ width: "100%" }}>
          <Upload
            accept=".png,.jpg,.jpeg,.svg,.webp"
            showUploadList={false}
            beforeUpload={handleBannerBeforeUpload}
            disabled={!!banner && !bannerError}
          >
            <Button icon={<UploadOutlined />} disabled={!!banner && !bannerError}>
              Upload
            </Button>
          </Upload>
          {!banner && !bannerError && <Text type="secondary">PNG/JPEG/WEBP/SVG up to 3 MB</Text>}
          {banner && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, border: "1px solid #f0f0f0", borderRadius: 4 }}>
              {getBannerSrc(banner) && (
                <img
                  src={getBannerSrc(banner)}
                  alt={banner.fileName || "Accreditation banner"}
                  style={{
                    width: 96,
                    height: 48,
                    objectFit: "cover",
                    borderRadius: 4,
                    border: "1px solid #f0f0f0",
                  }}
                />
              )}
              <div style={{ flex: 1 }}>
                <Text>{banner.fileName || "Banner image"}</Text>
              </div>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                onClick={handleRemoveBanner}
                aria-label="Remove accreditation banner"
              />
            </div>
          )}
        </Space>
      </Form.Item>

      <Form.Item label="Terms &amp; Conditions" name="termsAndConditions">
        <TextArea placeholder="Enter Terms & Conditions" autoSize={{ minRows: 3, maxRows: 8 }} />
      </Form.Item>

      <Form.Item label="Groups Payment Terms" name="groupsPaymentTerms">
        <TextArea placeholder="Enter Groups Payment Terms" autoSize={{ minRows: 3, maxRows: 8 }} />
      </Form.Item>

      <Form.Item
        label={
          <>
            <span style={{ fontWeight: 'bold', color: 'red', marginRight: 3 }}>Exclude</span>
            {' this division from the following booking systems:'}
          </>
        }
        name="excludedBookingSystemIds"
      >
        <Checkbox.Group
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
          disabled={bookingSystemsLoading}
        >
          {bookingSystems.map((system) => (
            <Checkbox key={system.id} value={String(system.id)}>
              {system.name}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>
                </>
              ),
            },
            {
              key: "report-text",
              label: "Report Text",
              children: activeTab === "report-text" ? (
                <Row gutter={16} style={{ minHeight: 400 }}>
                  <Col xs={24} md={8}>
                      <Table<ReportTextTemplate>
                      columns={documentTableColumns}
                        dataSource={reportTextTemplates}
                        rowKey="reportTextId"
                      pagination={false}
                      size="small"
                      onRow={(record) => ({
                        onClick: () => handleDocumentSelect(record.reportTextId),
                        style: {
                          cursor: "pointer",
                          backgroundColor: selectedDocumentId === record.reportTextId ? "#e6f7ff" : "transparent",
                        },
                      })}
                    />
                  </Col>
                  
                  <Col xs={24} md={16}>
                    {selectedDocument ? (
                      <div style={{ padding: "0 16px" }}>
                        <Title level={4} style={{ marginBottom: 8 }}>
                          {selectedDocument.name}
                        </Title>
                        {selectedDocument.usedIn && (
                          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
                            {selectedDocument.usedIn}
                          </Text>
                        )}

                        {selectedDocument.supportsRecipientType && (
                          <Form.Item label="Type" style={{ maxWidth: 320 }}>
                            <Select
                              value={recipientAudienceForSelected}
                              disabled={selectedDocumentLocksAgentsOnlyType}
                              onChange={(v) => {
                                if (selectedDocumentId == null) return;
                                setRecipientAudienceByReportTextId((prev) => ({
                                  ...prev,
                                  [selectedDocumentId]: v,
                                }));
                              }}
                              options={
                                selectedDocumentLocksAgentsOnlyType
                                  ? [{ value: "agents", label: "For Agents" }]
                                  : [
                                      { value: "students", label: "For Students" },
                                      { value: "agents", label: "For Agents" },
                                    ]
                              }
                              aria-label="Report text recipient type"
                            />
                          </Form.Item>
                        )}

                        <Form.Item
                          key={`${selectedDocument.reportTextId}-${
                            selectedDocument.supportsRecipientType
                              ? recipientAudienceForSelected
                              : "default"
                          }`}
                          name={[
                            "reportTextById",
                            String(selectedDocument.reportTextId),
                            selectedDocument.supportsRecipientType
                              ? recipientAudienceForSelected === "agents"
                                ? "agentContent"
                                : "studentContent"
                              : "content",
                          ]}
                          valuePropName="value"
                          trigger="onChange"
                          preserve
                        >
                          <RichTextEditor aria-label={selectedDocument.name} />
                        </Form.Item>
                        <Form.Item noStyle>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                              justifyContent: "space-between",
                              fontSize: 12,
                              marginTop: -16,
                              marginBottom: 16,
                            }}
                          >
                            <div></div>
                            <Text type="secondary">Character count: {currentDocumentCharCount}</Text>
                          </div>
                        </Form.Item>
                      </div>
                    ) : (
                      <div style={{ padding: "40px", textAlign: "center", color: "#999" }}>
                        <Text>Select a document from the list to edit</Text>
                      </div>
                    )}
                  </Col>
                </Row>
              ) : null,
            },
          ]}
        />
      </Form>

      <UnsavedChangesModal
        open={!!unsavedPrompt}
        onLeave={confirmUnsavedPrompt}
        onStay={dismissUnsavedPrompt}
      />
    </>
  );
};

export default DivisionDetailsPage;